import React, { FC, useCallback, useEffect, useRef } from 'react';

import { User } from 'oidc-client';
import { AuthService } from './AuthService';
import { AuthContext } from './AuthContext';
import { useAuthenticationContextState } from './AuthReducer';
import axios from 'axios';
import { Log } from '@utils/logger';

export interface AuthProviderProps {
  authService: AuthService;
  location?: Location;
  onSignIn?: (userData: User | null) => Promise<void> | void;
  handleApiError?: (error: any) => void;
}

export const AuthProvider: FC<AuthProviderProps> = ({
  children,
  authService,
  handleApiError,
  location = window.location
}) => {
  Log.debug('Auth Provider');

  const { authState, loadUser, onError, onLoading, onLogout } = useAuthenticationContextState();

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    const fn = async () => {
      try {
        onLoading();
        if (hasCodeInUrl(location)) {
          await authService.signInRedirectCallback();
        }

        const user = await authService.tryGetUser();
        if (isMounted.current) {
          loadUser(user);
        }
      } catch (error) {
        onError(error);
      }
    };

    fn();

    return () => {
      isMounted.current = false;
    };
  }, [onLoading, loadUser, onError, authService, location]);

  const signInSilent = useCallback(() => {
    Log.debug('UseCallback : signInSilent');
    onLoading();
    return authService.signInSilent().then((user) => {
      loadUser(user);
      return user;
    });
  }, [authService, loadUser, onLoading]);

  const signInRedirect = useCallback(
    (path?: string) => {
      Log.debug('UseCallback : signInRedirect');
      onLoading();
      authService.signInRedirect(path);
    },
    [authService, onLoading]
  );

  const signOut = useCallback(() => {
    Log.debug('UseCallback : signOut');
    onLoading();
    authService.signOut();
  }, [authService, onLoading]);

  const signOutRedirectCallback = useCallback(() => {
    Log.debug('UseCallback : signOutRedirectCallback');
    authService.signOutRedirectCallback();
    onLogout();
  }, [authService, onLogout]);

  const createAxiosResponseInterceptor = useCallback(() => {
    if (!authState.user) {
      return;
    }
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response.status !== 401) {
          Log.warn('Error', error.response);
          handleApiError?.(error);
          return Promise.reject(error);
        }

        axios.interceptors.response.eject(interceptor);
        return signInSilent()
          .then((user) => {
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.access_token;
            var axiosConfig = error.response.config;
            axiosConfig.headers.Authorization = 'Bearer ' + user.access_token;
            return axios(axiosConfig);
          })
          .catch((error) => {
            Log.debug('Axios: Sign in Redirect');
            signInRedirect();
            return Promise.reject(error);
          })
          .finally(createAxiosResponseInterceptor);
      }
    );
  }, [authState.user, signInRedirect, signInSilent]);

  createAxiosResponseInterceptor();

  return (
    <AuthContext.Provider
      value={{
        signInSilent,
        signInRedirect,
        signOut,
        signOutRedirectCallback,
        hasPermission: authService.hasPermission,
        ...authState
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const hasCodeInUrl = (location: Location): boolean => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

  return Boolean(
    searchParams.get('code') ||
      searchParams.get('id_token') ||
      searchParams.get('session_state') ||
      hashParams.get('code') ||
      hashParams.get('id_token') ||
      hashParams.get('session_state')
  );
};
